<template>
  <section class="ai-section ai-prompts-grid">
    <div class="ai-panel">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">Prompt library</h2>
          <p class="ai-panel__subtitle">Prompturile sunt per companie și sunt folosite de workflow-uri prin profile AI.</p>
        </div>
        <div class="ai-action-row ai-action-row--tight">
          <v-btn size="small" variant="tonal" color="primary" @click="startNewPrompt">
            Nou
          </v-btn>
          <v-btn icon variant="text" size="small" :loading="loading" @click="refreshPanel">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-if="loading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!prompts.length" class="ai-empty">
        Nu există prompturi configurate.
      </div>
      <div v-else class="ai-prompt-list">
        <button
          v-for="prompt in prompts"
          :key="prompt.prompt_key"
          type="button"
          class="ai-prompt-list__item"
          :class="{ 'ai-prompt-list__item--active': selectedPromptKey === prompt.prompt_key && !isNewPrompt }"
          @click="selectPrompt(prompt.prompt_key)"
        >
          <span class="ai-prompt-list__title">{{ prompt.display_name }}</span>
          <span class="ai-prompt-list__meta">{{ prompt.prompt_key }}</span>
          <span class="ai-prompt-list__footer">
            <v-chip size="x-small" :color="prompt.status === 'active' ? 'success' : 'grey'">
              {{ prompt.status }}
            </v-chip>
            <span>v{{ prompt.active_version }}</span>
          </span>
        </button>
      </div>
    </div>

    <div class="ai-panel ai-prompt-editor">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">{{ isNewPrompt ? "Prompt nou" : draft.display_name || "Prompt" }}</h2>
          <p class="ai-panel__subtitle">Setările modelului rămân legate de capability profile, dar pot fi ajustate per prompt.</p>
        </div>
        <v-chip v-if="!isNewPrompt && selectedPrompt?.active_version" color="primary">
          Versiune {{ selectedPrompt.active_version }}
        </v-chip>
      </div>

      <div class="ai-prompt-editor__grid">
        <v-text-field
          v-model="draft.prompt_key"
          :disabled="!isNewPrompt"
          label="Prompt key"
          hide-details
        />
        <v-text-field
          v-model="draft.display_name"
          label="Nume"
          hide-details
        />
        <v-select
          v-model="draft.status"
          :items="promptStatusOptions"
          item-title="label"
          item-value="value"
          label="Status"
          hide-details
        />
        <v-select
          v-model="draft.profile_key"
          :items="profileOptions"
          item-title="label"
          item-value="value"
          label="Capability profile"
          hide-details
          @update:model-value="applySelectedProfileDefaults"
        />
        <v-text-field
          v-model="draft.provider_key"
          label="Provider"
          hide-details
        />
        <v-text-field
          v-model="draft.model_key"
          label="Model"
          hide-details
        />
        <v-select
          v-model="draft.reasoning_effort"
          :items="reasoningOptions"
          item-title="label"
          item-value="value"
          label="Reasoning"
          hide-details
        />
        <v-select
          v-model="draft.text_verbosity"
          :items="textVerbosityOptions"
          item-title="label"
          item-value="value"
          label="Verbosity"
          hide-details
        />
        <v-text-field
          v-model="draft.timeout_ms"
          type="number"
          label="Timeout ms"
          hide-details
        />
        <v-text-field
          v-model="draft.entity_type"
          label="Entity type"
          hide-details
        />
      </div>

      <v-textarea
        v-model="draft.description"
        label="Descriere"
        rows="2"
        auto-grow
        hide-details
        class="mt-4"
      />

      <v-textarea
        v-model="draft.prompt_body"
        label="Prompt"
        rows="9"
        auto-grow
        hide-details
        class="mt-4 ai-prompt-editor__textarea"
      />

      <div class="ai-prompt-editor__json-grid">
        <v-textarea
          v-model="draft.tool_config_text"
          label="Tool config JSON"
          rows="7"
          auto-grow
          hide-details
          class="ai-prompt-editor__code"
        />
        <v-textarea
          v-model="draft.output_schema_text"
          label="Output schema JSON"
          rows="7"
          auto-grow
          hide-details
          class="ai-prompt-editor__code"
        />
        <v-textarea
          v-model="draft.retry_policy_text"
          label="Retry policy JSON"
          rows="7"
          auto-grow
          hide-details
          class="ai-prompt-editor__code"
        />
      </div>

      <v-textarea
        v-model="draft.notes"
        label="Note interne"
        rows="2"
        auto-grow
        hide-details
        class="mt-4"
      />

      <div class="ai-action-row">
        <v-btn color="primary" :loading="saving" @click="savePrompt">
          Salvează promptul
        </v-btn>
        <v-btn variant="tonal" color="primary" :disabled="saving" @click="resetDraft">
          Resetează
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { REASONING_OPTIONS } from "./aiWorkspaceShared.mjs";
import {
  PROMPT_STATUS_OPTIONS,
  TEXT_VERBOSITY_OPTIONS,
  buildNewPromptDraft,
  buildPromptDraft,
  serializePromptDraft,
} from "./aiPrompts.mjs";

export default {
  name: "AiPromptsPanel",
  emits: ["feedback"],
  data() {
    return {
      loading: false,
      saving: false,
      prompts: [],
      profiles: [],
      selectedPromptKey: "",
      isNewPrompt: false,
      draft: buildNewPromptDraft(),
      promptStatusOptions: PROMPT_STATUS_OPTIONS,
      textVerbosityOptions: TEXT_VERBOSITY_OPTIONS,
      reasoningOptions: REASONING_OPTIONS,
    };
  },
  computed: {
    selectedPrompt() {
      return this.prompts.find((prompt) => prompt.prompt_key === this.selectedPromptKey) || null;
    },
    selectedProfile() {
      return this.profiles.find((profile) => profile.profile_key === this.draft.profile_key) || null;
    },
    profileOptions() {
      return this.profiles.map((profile) => ({
        label: `${profile.display_name || profile.profile_key} (${profile.profile_key})`,
        value: profile.profile_key,
      }));
    },
  },
  mounted() {
    this.loadPrompts();
  },
  methods: {
    emitFeedback(type, message) {
      this.$emit("feedback", { type, message });
    },
    refreshPanel() {
      return this.loadPrompts();
    },
    async loadPrompts() {
      this.loading = true;
      try {
        const [promptsResponse, settingsResponse] = await Promise.all([
          axios.get("/ai/prompts"),
          axios.get("/ai/settings"),
        ]);
        this.prompts = Array.isArray(promptsResponse.data?.items) ? promptsResponse.data.items : [];
        this.profiles = Array.isArray(settingsResponse.data?.profiles) ? settingsResponse.data.profiles : [];
        if (this.isNewPrompt) {
          return;
        }
        const nextKey = this.selectedPromptKey || this.prompts[0]?.prompt_key || "";
        if (nextKey) {
          this.selectPrompt(nextKey);
        } else {
          this.startNewPrompt();
        }
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-au putut încărca prompturile AI.");
      } finally {
        this.loading = false;
      }
    },
    selectPrompt(promptKey) {
      const prompt = this.prompts.find((entry) => entry.prompt_key === promptKey);
      if (!prompt) {
        return;
      }
      this.isNewPrompt = false;
      this.selectedPromptKey = prompt.prompt_key;
      this.draft = buildPromptDraft(prompt);
    },
    startNewPrompt() {
      this.isNewPrompt = true;
      this.selectedPromptKey = "";
      this.draft = buildNewPromptDraft();
      this.applySelectedProfileDefaults();
    },
    applySelectedProfileDefaults() {
      const profile = this.selectedProfile;
      if (!profile) {
        return;
      }
      this.draft.provider_key = profile.provider_key || this.draft.provider_key;
      this.draft.model_key = profile.model_key || this.draft.model_key;
      this.draft.reasoning_effort = profile.reasoning_effort || this.draft.reasoning_effort;
    },
    resetDraft() {
      if (this.isNewPrompt) {
        this.startNewPrompt();
        return;
      }
      this.selectPrompt(this.selectedPromptKey);
    },
    async savePrompt() {
      const promptKey = String(this.draft.prompt_key || "").trim();
      if (!promptKey) {
        this.emitFeedback("error", "Prompt key este obligatoriu.");
        return;
      }
      this.saving = true;
      try {
        const payload = serializePromptDraft(this.draft);
        const { data } = await axios.put(`/ai/prompts/${promptKey}`, payload);
        this.emitFeedback("success", `Promptul ${promptKey} a fost salvat.`);
        this.isNewPrompt = false;
        this.selectedPromptKey = data?.prompt?.prompt_key || promptKey;
        await this.loadPrompts();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || error?.message || "Nu s-a putut salva promptul.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
